<template>
    <el-card>
        <div>ID: {{ cardParams.id }}
            <CopyButton v-if="cardParams.id" :text="cardParams.id" />
        </div>
        <el-collapse v-model="activeNames">
            <el-collapse-item title="基本信息" name="base">
                <el-form :model="cardParams" label-width="100px">
                    <el-row>
                        <el-col :span="12">
                            <el-form-item label="名称" prop="name">
                                {{ cardParams.name }}
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="类型" prop="type">
                                {{ CARD_TYPE[cardParams.type] }}
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-row>
                        <el-col :span="24">
                            <el-form-item label="描述" prop="desc">
                                {{ cardParams.desc }}
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-row>
                        <el-col :span="12">
                            <el-form-item label="图片" prop="image">
                                <div class="col-image">
                                    <img v-if="cardParams.image" :src="cardParams.image" alt="image" loading="lazy"
                                        @click="openPreview(cardParams.image)" />
                                </div>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="Video" prop="video">
                                <div class="col-image">
                                    <img v-if="cardParams.video" :src="cardParams.video" alt="video" loading="lazy" />
                                </div>
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <el-row>
                        <el-col :span="8">
                            <el-form-item label="稀有度" prop="rarity">
                                {{ cardParams.rarity }}
                            </el-form-item>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="分类" prop="categoryIds">
                                {{ cardParams.categoryNameStr }}
                            </el-form-item>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="标签" prop="tagIds">
                                {{ cardParams.tagNameStr }}
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-row>
                        <el-col :span="12">
                            <el-form-item :label="cardParams.type === 'card' ? '价值' : '数量'" prop="value">
                                {{ cardParams.value }}
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <el-row>
                        <el-col :span="12">
                            <el-form-item label="允许兑换积分" prop="allowExchange">
                                <el-icon><Select v-if="cardParams.allowExchange" />
                                    <CloseBold v-else />
                                </el-icon>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="允许邮寄提现" prop="allowShip">
                                <el-icon><Select v-if="cardParams.allowShip" />
                                    <CloseBold v-else />
                                </el-icon>
                            </el-form-item>
                        </el-col>
                    </el-row>

                </el-form>
            </el-collapse-item>
            <el-collapse-item title="当前拥有者" name="owners">
                <div class="table-wrapper">
                    <el-table :data="ownersInfo.rows" v-loading="ownersInfo.loading" style="width: 100%">
                        <el-table-column label="用户ID" prop="userId" width="200">
                            <template #default="scope">
                                <el-link underline="always" type="primary" @click="viewUser(scope.row.userId)">{{
                                    scope.row.userId
                                }}</el-link>
                            </template>
                        </el-table-column>
                        <el-table-column label="拥有张数" prop="count" width="120"></el-table-column>
                    </el-table>
                </div>
                <div class="pagination-wrapper">
                    <el-pagination v-model:current-page="ownersInfo.page" v-model:page-size="ownersInfo.size"
                        :page-sizes="[10, 20, 50]" :total="ownersInfo.total"
                        layout="total, sizes, prev, pager, next, jumper" @current-change="loadOwners"
                        @size-change="loadOwners" />
                </div>
            </el-collapse-item>
        </el-collapse>
    </el-card>
    <el-image-viewer v-if="showPreview" :url-list="showList" :initial-index="previewIndex" @close="showPreview = false"
        :hide-on-click-modal="true" />
</template>

<script setup>
import { ref, reactive, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiCall } from "@/utils/api";
import { CARD_TYPE } from "@/utils/constant";
import { useCardCategoryStore } from "@/stores/card/category";
import { useCardTagStore } from "@/stores/card/tag";

const cardCategoryStore = useCardCategoryStore();
const cardTagStore = useCardTagStore();
const route = useRoute();
const router = useRouter();

const loading = ref(false);

const activeNames = ref(['base']);

watch(
    () => activeNames,
    (newAns) => {
        const ans = newAns.value || [];
        if (ans.includes("owners") && !ownersInfo.loaded) {
            loadOwners();
        }
    },
    { deep: true }
);

const cardParams = reactive({
    id: "",
    name: "",
    type: "",
    desc: "",
    image: "",
    video: "",
    categoryIds: [],
    tagIds: [],
    rarity: "",
    value: 0,
    allowExchange: true,
    allowShip: true,
    categoryNameStr: "",
    tagNameStr: "",
});

const showList = ref([]);
const showPreview = ref(false);
const previewIndex = ref(0);

const openPreview = (url) => {
    showList.value = [url];
    previewIndex.value = 0;
    showPreview.value = true;
}

const ownersInfo = reactive({
    loading: false,
    loaded: false,
    rows: [],
    page: 1,
    size: 10,
    total: 0,
});

async function loadOwners() {
    const { id } = route.params;

    ownersInfo.loading = true;
    const params = {
        cardId: id,
        page: ownersInfo.page,
        size: ownersInfo.size,
    };

    const response = await apiCall("card.getCardOwners", params);
    if (!response) {
        ownersInfo.loading = false;
        return false;
    }
    ownersInfo.rows = response.rows;
    ownersInfo.total = response.count;

    ownersInfo.loading = false;
    ownersInfo.loaded = true;
}

function viewUser(id) {
    router.push(`/user/${id}`);
}

async function loadData(id) {
    loading.value = true;
    const response = await apiCall("card.getCard", { id });
    if (!response) {
        loading.value = false;
        return false;
    }

    response.categoryNameStr = response.categoryIds.map(c => cardCategoryStore.categoryObj[c]).join(",");
    response.tagNameStr = response.tagIds.map(t => cardTagStore.tagObj[t]).join(",");
    response.allowExchange = response.allowExchange === 1;
    response.allowShip = response.allowShip === 1;

    Object.keys(cardParams).forEach(key => {
        cardParams[key] = response[key];
    });

    loading.value = false;
}

onMounted(async () => {
    if (!route.params.id) {
        alert("empty id");
        return router.go(-1);
    }
    await cardCategoryStore.getAll();
    await cardTagStore.getAll();
    await loadData(route.params.id);
});
</script>

<style scoped>
.basic-part .el-row {
    margin-bottom: 10px;
}

.col-image {
    position: relative;
    width: 180px;
    height: 180px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
}

.col-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.col-image:hover img {
    transform: scale(1.05);
}
</style>
